import type { BtlApplication } from 'api/btlApplicationForm/types';
import { shouldBlockSubmissionForMissingFields } from 'common/missingFields';
import { monthsToYears } from 'utils/numbers';
import { resolveMissingFieldsWarnings, resolvePropertyStatusWarnings } from './missingFieldsWarnings';
import type { BuildBtlCaseSidebarParams } from '../types';
import { CaseSidebarItemId } from '../types';

function formatFtv(ftv?: number): string {
  if (ftv === undefined || ftv === null) {
    return '';
  }

  return `FTV: ${(ftv * 100).toFixed(0)}%`;
}

export function resolveItemSubTitle(itemId: CaseSidebarItemId, application: BtlApplication): string {
  switch (itemId) {
    case CaseSidebarItemId.FINANCING: {
      const purpose = application.purpose
        ? application.purpose.charAt(0).toUpperCase() + application.purpose.slice(1).toLowerCase()
        : '';
      const ftv = formatFtv(application.netFinancingFtv);

      return [purpose, ftv].filter(Boolean).join(' / ');
    }
    case CaseSidebarItemId.APPLICANTS:
      return '';
    case CaseSidebarItemId.PRODUCT: {
      const product = application.product;

      if (!product) {
        return '';
      }

      const term = monthsToYears(product.monthsInitial);
      const ftv = (product.maximumFTV * 100).toFixed(0);
      const productType = product.productType?.toLowerCase() ?? '';

      return `${term}yr ${productType} ${ftv}% FTV`;
    }
    default:
      return '';
  }
}

export function resolveItemIsDisabled(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): boolean {
  const { application, isAffordabilityPassed, isMissingFieldsComplete, missingFields } = context;
  const companiesCount = application.borrower?.companies.length ?? 0;
  const individualsCount = application.borrower?.individuals.length ?? 0;
  const isBlockedByMissingFields = shouldBlockSubmissionForMissingFields(
    application,
    missingFields,
    isMissingFieldsComplete,
  );

  switch (itemId) {
    case CaseSidebarItemId.APPLICANTS:
      return !application.id;
    case CaseSidebarItemId.PRODUCT:
      return companiesCount + individualsCount === 0;
    case CaseSidebarItemId.TOP_SLICING:
    case CaseSidebarItemId.AFFORDABILITY_CHECK:
      return !application.product;
    case CaseSidebarItemId.SUBMIT_DIP:
      return !isAffordabilityPassed || isBlockedByMissingFields;
    case CaseSidebarItemId.SUBMIT_APPLICATION:
    case CaseSidebarItemId.ADDITIONAL_INFORMATION:
      return isBlockedByMissingFields;
    default:
      return false;
  }
}

function isSubmissionSidebarItem(itemId: CaseSidebarItemId): boolean {
  return (
    itemId === CaseSidebarItemId.SUBMIT_DIP ||
    itemId === CaseSidebarItemId.SUBMIT_APPLICATION ||
    itemId === CaseSidebarItemId.ADDITIONAL_INFORMATION
  );
}

export function resolveItemWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  const isBlockedByMissingFields = shouldBlockSubmissionForMissingFields(
    context.application,
    context.missingFields,
    context.isMissingFieldsComplete,
  );

  if (isSubmissionSidebarItem(itemId) && isBlockedByMissingFields) {
    return [context.formatMessage({ id: 'btlApplicationForm.submit.missingFields.blocked' })];
  }

  if (resolveItemIsDisabled(itemId, context)) {
    return [];
  }

  const warnings: string[] = [];

  warnings.push(...resolveMissingFieldsWarnings(itemId, context));
  warnings.push(...resolvePropertyStatusWarnings(itemId, context));

  if (itemId === CaseSidebarItemId.AFFORDABILITY_CHECK && context.isAffordabilityRunRequired) {
    warnings.push(context.formatMessage({ id: 'caseSidebar.warnings.affordabilityCheckOutdated' }));
  }

  return warnings;
}
